import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import type { ShopCatalogResponse } from "@gooner/shared";
import { track } from "../../analytics";
import { userApi } from "../../lib/api";
import { useSession } from "../../lib/session";
import { ShopBackLink } from "../components/ShopBackLink";
import { ProductGrid, ShopActions } from "./Shop";

/** `/shop/category/:category` — a section's See all: every live product in that category. */
export function ShopCategoryPage() {
  const { category = "" } = useParams();
  const user = useSession((s) => s.user);
  const [catalog, setCatalog] = useState<ShopCatalogResponse | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    userApi
      .shopCatalog()
      .then((r) => {
        if (cancelled) return;
        setCatalog(r);
        const count = r.products.filter((p) => p.category === category).length;
        if (r.open) track("shop_category_viewed", { category, products: count });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [category, user?.id]);

  const section = catalog?.categories.find((c) => c.slug === category);
  const products = catalog?.products.filter((p) => p.category === category) ?? [];

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <ShopBackLink />
        <ShopActions />
      </div>
      {failed || (catalog && !catalog.open) ? (
        <p className="py-16 text-center text-sm text-zinc-500">
          {failed ? "The shop didn't load. Reload the page to try again." : "The shop isn't open yet."}
        </p>
      ) : null}
      {catalog?.open && !section ? (
        <p className="py-16 text-center text-sm text-zinc-500">That section isn't in the shop any more.</p>
      ) : null}
      {section ? (
        <>
          <h1 className="mt-5 text-2xl font-semibold text-zinc-100">{section.name}</h1>
          {section.blurb ? <p className="mt-1 text-sm text-zinc-400">{section.blurb}</p> : null}
          <p className="mt-1 text-sm text-zinc-500">
            {products.length} {products.length === 1 ? "product" : "products"}
          </p>
          <div className="mt-5">
            <ProductGrid products={products} from="category" />
          </div>
        </>
      ) : null}
    </div>
  );
}
